import { useEffect, useState } from "react";
import { Play, Plus, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { getToken, fetchVideos, createVideo, deleteVideo } from "@/lib/api";
import { AdSlot } from "@/components/AdSlot";

const CATS = ["music", "athletes", "fashion", "shoes", "cars", "travel", "technology", "business", "media", "food"];

export default function Watch() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ youtube_url: "", title: "", category: "music", description: "" });
  const authed = !!getToken();

  const load = () => fetchVideos().then(setVideos).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.youtube_url.trim() || !form.title.trim()) {
      toast.error("Title and YouTube URL are required.");
      return;
    }
    setSaving(true);
    try {
      await createVideo(form);
      toast.success("Video added.");
      setForm({ youtube_url: "", title: "", category: "music", description: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add video.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteVideo(id);
      setVideos((v) => v.filter((x) => x.id !== id));
      toast.success("Video removed.");
    } catch {
      toast.error("Failed to remove.");
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Watch</span>
      <h1 className="font-anton text-6xl md:text-8xl uppercase tracking-tight mt-3 leading-none" data-testid="watch-title">
        The Screen
      </h1>
      <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
        Video is where the culture moves loudest. Films, performances and behind-the-scenes
        from the world of NERO SUN.
      </p>

      <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_WATCH} className="mt-10" />

      {authed && (
        <div className="border border-white/10 bg-card p-6 mt-10 space-y-4">
          <div className="font-anton text-xl uppercase tracking-tight">Add a Video</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={label}>YouTube URL or ID</label>
              <input data-testid="video-url-input" value={form.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/watch?v=…" className={inputCls} />
            </div>
            <div>
              <label className={label}>Title</label>
              <input data-testid="video-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Video title" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={label}>Section</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CATS.map((c) => (
                <button key={c} onClick={() => set("category", c)} className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${form.category === c ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"}`}>{c}</button>
              ))}
            </div>
          </div>
          <button data-testid="video-add-btn" onClick={add} disabled={saving} className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Video
          </button>
        </div>
      )}

      {videos.length === 0 ? (
        <p className="font-mono text-sm text-muted-foreground mt-10" data-testid="watch-empty">No videos yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {videos.map((v, i) => (
            <div key={v.id} data-testid={`video-card-${v.id}`} className="group border border-white/10 bg-card hover:border-signal transition-colors animate-rise" style={{ animationDelay: `${i * 60}ms` }}>
              <button onClick={() => setActive(v)} className="relative block w-full aspect-video overflow-hidden" aria-label={`Play ${v.title}`}>
                <img src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="img-treatment w-full h-full object-cover" loading="lazy" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-signal/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-white ml-1" fill="white" />
                  </span>
                </span>
                <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-signal text-white px-2 py-1">{v.category}</span>
              </button>
              <div className="p-5 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold tracking-tight leading-tight group-hover:text-signal transition-colors">{v.title}</h3>
                  {v.description && <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{v.description}</p>}
                </div>
                {authed && (
                  <button data-testid={`video-delete-${v.id}`} onClick={() => remove(v.id)} className="text-muted-foreground hover:text-signal shrink-0" aria-label="Delete video"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setActive(null)} data-testid="video-modal">
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-anton text-xl uppercase tracking-tight">{active.title}</h3>
              <button data-testid="video-modal-close" onClick={() => setActive(null)} className="text-foreground/70 hover:text-signal"><X className="w-6 h-6" /></button>
            </div>
            <div className="aspect-video w-full border border-white/10">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                title={active.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
